import React, { useEffect, useState } from 'react'

import { Tab, Tabs, useTheme } from '@material-ui/core'

import { Container, SideDrawerContainer } from '../../components'
import { useAppDispatch, useAptConfig, useSchedule, useUser } from '../../hooks'
import {
    INCIDENT_REPORTS,
    INSPECTION_PREVIEW_EDITOR,
    InspectionType,
    TenantNegligibleFilter,
} from '../../models'
import { getInspectionList } from '../../store'
import { InspectionConfigurationModal } from './InspectionConfiguration/InspectionConfigurationModal'
import { hasPermission } from '../../models/Users/services'
import { IncidentReportRoot } from './IncidentReportRoot'
import { useInspectionFrequency } from '../../hooks/useInspectionFrequency'
import { InspectionPage } from './InspectionPage'
import { InspectionTypePage } from './InspectionTypePage'
import { InspectionFrequencyPage } from './InspectionFrequencyPage'
import { ActionItemTriggerPage } from './ActionItemTriggerPage'
import { StatusGroupConfig } from '../StatusGroupConfig'
import { InventoryConfigPage } from '../InfrastructureConfig/InventoryConfigPage'
import { useHistory, useLocation } from 'react-router-dom'
import { FlagInventoryInspectionsHome } from './FlagInventoryInspections/FlagInventoryInspectionsHome'

const INSPECTION_TAB = 0
const INSPECTION_TYPE_TAB = 1
const FLAG_INVENTORY_INSPECTIONS_TAB = 2
const INSPECTION_FREQUENCIES = 3
const INVENTORY_TYPES = 4
const STATUS_GROUPS = 5
const ACTION_ITEM_TRIGGER_TAB = 6
const INCIDENT_REPORT_TAB = 7

const TabHashMap: { [key: number]: string } = {
    [INSPECTION_TAB]: 'home',
    [INSPECTION_TYPE_TAB]: 'types',
    [FLAG_INVENTORY_INSPECTIONS_TAB]: 'flag-inventory-inspections',
    [INSPECTION_FREQUENCIES]: 'frequencies',
    [INVENTORY_TYPES]: 'inventory',
    [STATUS_GROUPS]: 'status-groups',
    [ACTION_ITEM_TRIGGER_TAB]: 'triggers',
    [INCIDENT_REPORT_TAB]: 'incidents',
}

export const InspectionRoot = () => {
    const history = useHistory()
    const dispatch = useAppDispatch()
    const location = useLocation()

    const hashToTab = Object.fromEntries(
        Object.entries(TabHashMap).map(([key, value]) => [value, Number(key)]),
    )

    const {
        inspectionTypeList,
        inventoryConfigList,
        customStatusList,
        areaConfigList,
        statusGroupList,
        damageConfigList,
        getAreaConfigMap,
    } = useAptConfig({
        inspectionTypeList: true,
        inventoryConfigList: true,
        areaConfigList: true,
        customStatusList: true,
        damageConfigList: true,
        statusGroupList: true,
        cleanUp: true,
    })

    const {
        frequencyList,
        setFrequencyList,
        getFrequencyList,
        selectedFrequency,
        setSelectedFrequency,
        toggleFrequencyActive,
    } = useInspectionFrequency()

    const areaConfigMap = getAreaConfigMap()

    const { scheduleList, getScheduleList } = useSchedule()

    const theme = useTheme()

    const [selectedTab, setSelectedTab] = useState(() => {
        const hash = location.hash.replace('#', '')
        return hash in hashToTab ? hashToTab[hash] : INSPECTION_TAB
    })

    const [drawerOpen, setDrawerOpen] = useState(false)

    const [selectedInspectionType, setSelectedInspectionType] = useState<
        InspectionType | undefined
    >()

    const [
        openInspectionConfiguration,
        setOpenInspectionConfiguration,
    ] = useState(false)
    const [initialModalTab, setInitialModalTab] = useState<number | undefined>(
        undefined,
    )

    const [incidentReportFilters, setIncidentReportFilters] = useState({
        status: -1,
        incidentType: -1,
        tenantNegligible: TenantNegligibleFilter.NO_FILTER,
    })

    useEffect(() => {
        getScheduleList({ params: { no_inspection: true, active: true } })
        getFrequencyList()
    }, [])

    useEffect(() => {
        const hash = location.hash.replace('#', '')
        if (hash in hashToTab) {
            setSelectedTab(hashToTab[hash])
        }
    }, [location])

    const { workspaceUser } = useUser()

    const [moveOutInspection, setMoveOutInspection] = useState<
        number | undefined
    >()

    useEffect(() => {
        if (workspaceUser?.active_workspace.move_out_inspection_config) {
            setMoveOutInspection(
                workspaceUser.active_workspace.move_out_inspection_config,
            )
        }
    }, [])

    const hasEditPermission = hasPermission(
        workspaceUser,
        INSPECTION_PREVIEW_EDITOR,
    )

    const handleTabChange = (_: any, newValue: number) => {
        if (newValue !== selectedTab) {
            setSelectedTab(newValue)
            history.push(`/inspect#${TabHashMap[newValue]}`)
        }
    }

    const handleOpenInspectionConfiguration = (
        open: boolean,
        initialTab?: number,
    ) => {
        setOpenInspectionConfiguration(open)
        setInitialModalTab(initialTab)
    }

    return (
        <SideDrawerContainer open={drawerOpen}>
            <Container
                style={{
                    flex: 1,
                    flexDirection: 'column',
                    height: 'calc(100vh - 104px)',
                    overflowY: 'hidden',
                    overflowX: 'auto',
                    width: '100%',
                }}
            >
                <Tabs
                    style={{
                        width: '100%',
                        backgroundColor: theme.palette.grey[300],
                    }}
                    value={selectedTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <Tab
                        label="Inspections"
                        style={{ textTransform: 'none' }}
                    />
                    <Tab
                        label="Inspection Types"
                        style={{ textTransform: 'none' }}
                    />
                    <Tab label="Flags" style={{ textTransform: 'none' }} />
                    <Tab
                        label="Inspection Frequencies"
                        style={{ textTransform: 'none' }}
                    />
                    <Tab
                        label="Inventory Types"
                        style={{ textTransform: 'none' }}
                    />
                    <Tab
                        label="Status Groups"
                        style={{ textTransform: 'none' }}
                    />
                    <Tab
                        label="Action Item Triggers"
                        style={{ textTransform: 'none' }}
                    />
                    {hasPermission(workspaceUser, INCIDENT_REPORTS) && (
                        <Tab
                            label="Incident Reports"
                            style={{ textTransform: 'none' }}
                        />
                    )}
                </Tabs>

                {selectedTab === INSPECTION_TAB && (
                    <InspectionPage
                        scheduleList={scheduleList}
                        inspectionTypeList={inspectionTypeList ?? []}
                        setOpenInspectionConfiguration={
                            setOpenInspectionConfiguration
                        }
                    />
                )}

                {selectedTab === INSPECTION_TYPE_TAB && (
                    <InspectionTypePage
                        inspectionTypeList={inspectionTypeList ?? []}
                        moveOutInspection={moveOutInspection}
                        selectedInspectionType={selectedInspectionType}
                        setSelectedInspectionType={setSelectedInspectionType}
                        setOpenInspectionConfiguration={
                            handleOpenInspectionConfiguration
                        }
                    />
                )}

                {selectedTab === FLAG_INVENTORY_INSPECTIONS_TAB && (
                    <FlagInventoryInspectionsHome />
                )}

                {selectedTab === INSPECTION_FREQUENCIES && (
                    <InspectionFrequencyPage
                        frequencyList={frequencyList}
                        areaConfigMap={areaConfigMap}
                        selectedFrequency={selectedFrequency}
                        setSelectedFrequency={setSelectedFrequency}
                        toggleFrequencyActive={toggleFrequencyActive}
                        setOpenInspectionConfiguration={
                            setOpenInspectionConfiguration
                        }
                        setFrequencyList={setFrequencyList}
                    />
                )}

                {selectedTab === ACTION_ITEM_TRIGGER_TAB && (
                    <ActionItemTriggerPage
                        inventoryConfigList={inventoryConfigList}
                        customStatusList={customStatusList}
                    />
                )}
                {selectedTab === INCIDENT_REPORT_TAB && (
                    <IncidentReportRoot
                        incidentReportFilters={incidentReportFilters}
                        onIncidentSelect={() => {
                            setDrawerOpen(true)
                        }}
                        setFilters={setIncidentReportFilters}
                    />
                )}

                {selectedTab === STATUS_GROUPS && (
                    <StatusGroupConfig
                        statusGroupList={statusGroupList ?? []}
                    />
                )}

                {selectedTab === INVENTORY_TYPES && (
                    <InventoryConfigPage
                        inventoryConfigList={inventoryConfigList ?? []}
                        areaConfigList={areaConfigList ?? []}
                        statusGroupList={statusGroupList ?? []}
                        damageConfigList={damageConfigList ?? []}
                    />
                )}
            </Container>

            <InspectionConfigurationModal
                open={openInspectionConfiguration}
                inspectionTypeList={inspectionTypeList ?? []}
                scheduleList={scheduleList}
                frequencyList={frequencyList}
                inspectionType={selectedInspectionType}
                inventoryConfigList={inventoryConfigList ?? []}
                areaConfigList={areaConfigList ?? []}
                statusGroupList={statusGroupList ?? []}
                hasEditPermission={hasEditPermission}
                setMoveOutInspection={setMoveOutInspection}
                setFrequencyList={setFrequencyList}
                getScheduleList={getScheduleList}
                initialTab={initialModalTab}
                handleClose={() => {
                    setOpenInspectionConfiguration(false)
                    setSelectedInspectionType(undefined)
                    setInitialModalTab(undefined)
                }}
            />
        </SideDrawerContainer>
    )
}
